import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useParams, Navigate } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { Toaster } from "sonner";
import { CookieConsent } from "@/components/site/CookieConsent";
import { BIFOLD_AREAS } from "@/data/bifoldAreas";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import Home from "@/pages/Home";

const TradeSupply = lazy(() => import("@/pages/TradeSupply"));
const SupplyInstall = lazy(() => import("@/pages/SupplyInstall"));
const ProductsPage = lazy(() => import("@/pages/ProductsPage"));
const ProductDetail = lazy(() => import("@/pages/ProductDetail"));
const About = lazy(() => import("@/pages/About"));
const Contact = lazy(() => import("@/pages/Contact"));
const Visualiser = lazy(() => import("@/pages/Visualiser"));
const Admin = lazy(() => import("@/pages/Admin"));
const BifoldLanding = lazy(() => import("@/pages/BifoldLanding"));
const Legal = lazy(() => import("@/pages/Legal"));

const BARE_ROUTES = ["/bifold-doors", "/privacy", "/terms"];

const AreaLanding = () => {
  const { areaSlug } = useParams();
  const a = BIFOLD_AREAS.find((x) => x.slug === areaSlug);
  return a ? <BifoldLanding area={a} /> : <Navigate to="/bifold-doors" replace />;
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const PageLoader = () => (
  <div className="min-h-[60vh] flex items-center justify-center bg-[#F8FAFC]" data-testid="page-loader">
    <div className="w-10 h-10 rounded-full border-4 border-slate-200 border-t-[#29CC9A] animate-spin" />
  </div>
);

const Shell = () => {
  const { pathname } = useLocation();
  const bare = pathname.startsWith("/bifold-doors") || BARE_ROUTES.includes(pathname.replace(/\/+$/, "") || "/");
  return (
    <>
      {!bare && <Navbar />}
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/bifold-doors" element={<BifoldLanding />} />
          <Route path="/bifold-doors/:areaSlug" element={<AreaLanding />} />
          <Route path="/trade-supply" element={<TradeSupply />} />
          <Route path="/supply-installation" element={<SupplyInstall />} />
          <Route path="/products" element={<ProductsPage />} />
          <Route path="/products/:slug" element={<ProductDetail />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/colour-visualiser" element={<Visualiser />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/privacy" element={<Legal />} />
          <Route path="/terms" element={<Legal />} />
          <Route path="*" element={<Home />} />
        </Routes>
      </Suspense>
      {!bare && <Footer />}
    </>
  );
};

function App() {
  return (
    <HelmetProvider>
      <Helmet>
        {process.env.REACT_APP_GSC_VERIFICATION && <meta name="google-site-verification" content={process.env.REACT_APP_GSC_VERIFICATION} />}
        {process.env.REACT_APP_BING_VERIFICATION && <meta name="msvalidate.01" content={process.env.REACT_APP_BING_VERIFICATION} />}
      </Helmet>
      <BrowserRouter>
        <ScrollToTop />
        <CookieConsent />
        <Shell />
        <Toaster position="bottom-right" richColors />
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
